import { Box, Typography } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const COLORS = [
  "#1565C0",
  "#EF6C00",
  "#2E7D32",
  "#6A1B9A",
  "#F9A825",
  "#00838F",
  "#AD1457",
  "#4E342E",
];

const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 18;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#333"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  );
};

const MiniPie = ({ title, breakdown, dimensionKey, onSliceClick }) => {
  const chartData = (breakdown || []).map((b) => ({
    name: b.name,
    value: b.count,
  }));

  if (chartData.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: "11px", color: "#999" }}>
          No data
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography
        sx={{ fontSize: "13px", fontWeight: 600, color: "#333", mb: 0.5 }}
      >
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={35}
            outerRadius={60}
            dataKey="value"
            paddingAngle={2}
            label={renderCustomLabel}
            labelLine={false}
          >
            {chartData.map((entry, i) => (
              <Cell
                key={i}
                fill={COLORS[i % COLORS.length]}
                style={{ cursor: onSliceClick ? "pointer" : "default" }}
                onClick={() =>
                  onSliceClick && onSliceClick(dimensionKey, entry.name)
                }
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(val, name) => [`${val} employees`, name]}
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
          />
        </PieChart>
      </ResponsiveContainer>
      {/* Legend below */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "6px 12px",
          mt: -0.5,
        }}
      >
        {chartData.slice(0, 6).map((item, i) => (
          <Box
            key={i}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: COLORS[i % COLORS.length],
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{ fontSize: "10px", color: "#555", whiteSpace: "nowrap" }}
            >
              {item.name} ({item.value})
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default function EmployeeDemographics({ data, onSliceClick }) {
  const demo = data?.employeeDemographics;

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "16px",
        p: 2.5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        height: "100%",
      }}
    >
      <Typography
        sx={{ fontWeight: 700, fontSize: "16px", color: "#333", mb: 1.5 }}
      >
        Employee Demographics
        {demo?.total != null && (
          <Typography
            component="span"
            sx={{ fontSize: "12px", color: "#888", ml: 1 }}
          >
            ({demo.total} active)
          </Typography>
        )}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
        }}
      >
        <MiniPie
          title="By Branch"
          breakdown={demo?.byBranch}
          dimensionKey="branch"
          onSliceClick={onSliceClick}
        />
        <MiniPie
          title="By Type"
          breakdown={demo?.byEmployeeType}
          dimensionKey="employeeType"
          onSliceClick={onSliceClick}
        />
        <MiniPie
          title="By Category"
          breakdown={demo?.byCategory}
          dimensionKey="category"
          onSliceClick={onSliceClick}
        />
        <MiniPie
          title="By Gender"
          breakdown={demo?.byGender}
          dimensionKey="gender"
          onSliceClick={onSliceClick}
        />
      </Box>
    </Box>
  );
}
